import React, { useEffect, useRef, useState } from "react";
import api from "../../api.js";
import { useCompanyApi } from "../../hooks/useCompanyApi.js"; // import OptionSelect from "./components/OptionSelect";
import BrandLogoUpload from "./components/BrandLogoUpload.jsx";
import ColorPicker from "./components/ColorPicker.jsx";

import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import CenterWrapper from "../../components/CenterWrapper.jsx";
import GeneralSetupTab from "../../components/GeneralSetupTab.jsx";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { DEFAULT_COLOR, MAX_SMALL_FILE_SIZE } from "../../utils/constants";
import TextArea from "../../components/TextArea";
import Input from "../../components/Input";
import { Tooltip } from "flowbite-react";
import { Link } from "react-router-dom";
import ChatWidget from "../../components/ChatWidget";
import useWindowSize from "../../hooks/useWindowSize";
import ChatWidgetModal from "../../components/ChatWidgetModal";
import ChatButton from "../../icons/ChatButton";

const Branding = () => {
  const windowSize = useWindowSize();

  const [showWidgetModal, setShowWidgetModal] = useState(false);

  const onCloseWidgetModal = () => {
    setShowWidgetModal(false);
  };

  const [chatWidgetKey, setChatWidgetKey] = useState(1);

  const chatbotUuid = localStorage.getItem("chatbot_uuid") || "";

  const { company, fetchCompany, updateCompany, setLoading } = useCompanyApi();

  const { setValue, register, watch } = useForm({
    defaultValues: {
      companyImageUrl: "",
      chatbotPictureUrl: "/avatar.png",
      hand_off_within_hours: "",
      hand_off_outside_hours: "",
      greeting: "",
      prompts: "",
      companyName: "",
      aiEmployeeName: "",
      email: "",
      chatButtonColor: DEFAULT_COLOR,
      buttonColor: DEFAULT_COLOR,
    },
  });

  const chatButtonColor = watch("chatButtonColor");
  const buttonColor = watch("buttonColor");
  const companyName = watch("companyName");
  const companyImageUrl = watch("companyImageUrl");
  const chatbotPictureUrl = watch("chatbotPictureUrl");

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/api/chatbot/${chatbotUuid}`);
      const chatbot = response.data;

      setValue("hand_off_within_hours", chatbot.hand_off_within_hours);
      setValue("hand_off_outside_hours", chatbot.hand_off_outside_hours);
      setValue("greeting", chatbot.greeting);
      setValue("prompts", chatbot.prompt_text);
      setValue("aiEmployeeName", chatbot.name);
      setValue("email", chatbot.email);

      if (chatbot.chat_color) {
        setValue("chatButtonColor", `#${chatbot.chat_color}`);
      }

      if (chatbot.button_color) {
        setValue("buttonColor", `#${chatbot.button_color}`);
      }
    } catch (err) {
      console.error("Failed to fetch settings", err);
    }
  };

  useEffect(() => {
    if (chatbotUuid) {
      api.get(`/api/chatbot/${chatbotUuid}`).then(response => {
        if (response.data.picture_url) {
          setValue("chatbotPictureUrl", response.data.picture_url);
        }
      });

      fetchCompany();
      fetchSettings();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotUuid]);

  useEffect(() => {
    if (company) {
      setValue("companyName", company.name);
      setValue("companyImageUrl", company.picture_url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const uploadChatbotFile = async file => {
    if (file.size > MAX_SMALL_FILE_SIZE) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Upload failed",
        submsg: "File size exceeds 1MB limit.",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/api/chatbot/${chatbotUuid}/upload_picture`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.url) {
        setValue("chatbotPictureUrl", response.data.url);
      }

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Upload completed",
        submsg: "Company picture uploaded!",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Upload failed",
        submsg: "Please try again later.",
      });
    } finally {
      setLoading(false);
      setTimeout(() => setFeedback({ visible: false }), 10000);
    }
  };

  const uploadCompanyFile = async file => {
    // Reject if file is larger than 1MB
    if (file.size > MAX_SMALL_FILE_SIZE) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Upload failed",
        submsg: "File size exceeds 1MB limit.",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/api/company/upload_picture`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setValue("companyImageUrl", response.data.url);

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Upload completed",
        submsg: "Company picture uploaded!",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Upload failed",
        submsg: "Please try again later.",
      });
    } finally {
      setLoading(false);
      setTimeout(() => setFeedback({ visible: false }), 10000);
    }
  };

  const changeColorDebounced = useDebouncedCallback(async (name, value) => {
    try {
      await api.put(`/api/chatbot/${chatbotUuid}`, {
        [name]: value.substring(1),
      });

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Successfully saved",
        submsg: "Changes are saved automatically.",
      });

      setChatWidgetKey(prev => prev + 1);
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Please try again later!",
      });
    }
  }, 1000);

  const handleChatButtonColorChanged = newColor => {
    setValue("chatButtonColor", newColor);
    changeColorDebounced("chat_color", newColor);
  };

  const handleButtonColorChanged = async newColor => {
    setValue("buttonColor", newColor);
    changeColorDebounced("button_color", newColor);
  };

  const debounceTimeout = useRef(null);

  const savePrompts = async newPromptsValue => {
    try {
      await api.post(`/api/prompts/${chatbotUuid}`, { text: newPromptsValue });
      setFeedback({
        visible: true,
        status: "ok",
        msg: "Successfully saved",
        submsg: "Changes are saved automatically.",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Please try again later!",
      });
    }
  };

  const handleBrandVoiceChanged = e => {
    const value = e.target.value;

    setValue("prompts", value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => savePrompts(value), 600);
  };

  const saveGreeting = async newGreetingValue => {
    try {
      await api.put(`/api/chatbot/${chatbotUuid}/greeting`, {
        greeting: newGreetingValue,
      });

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Successfully saved",
        submsg: "Changes are saved automatically.",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Please try again later!",
      });
    }
  };

  const saveChatbotChangesDebounced = useDebouncedCallback(
    async event => {
      try {
        await api.put(`/api/chatbot/${chatbotUuid}`, {
          [event.target.name]: event.target.value,
        });

        setFeedback({
          visible: true,
          status: "ok",
          msg: "Successfully saved",
          submsg: "Changes are saved automatically.",
        });
      } catch (err) {
        setFeedback({
          visible: true,
          status: "error",
          msg: "Save failed",
          submsg: "Please try again later!",
        });
      }
    },
    // delay in ms
    600
  );

  const handleIntroMesageChanged = e => {
    const value = e.target.value;

    setValue("greeting", value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => saveGreeting(value), 600);
  };

  const abortIfEmptyValue = value => {
    if (value === "") {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Field cannot be empty!",
      });

      return true;
    }

    return false;
  };

  const handleEmailChanged = e => {
    const value = e.target.value;

    if (abortIfEmptyValue(value)) {
      return;
    }

    setValue("email", value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => saveChatbotEmail(value), 600);
  };

  const handleAiEmployeeNameChanged = e => {
    const value = e.target.value;

    if (abortIfEmptyValue(value)) {
      return;
    }

    setValue("aiEmployeeName", value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => saveChatbotName(value), 600);
  };

  const handleCompanyChanged = e => {
    const value = e.target.value;

    if (abortIfEmptyValue(value)) {
      return;
    }

    setValue("companyName", value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => saveCompanyName(value), 600);
  };

  const saveChatbotEmail = async email => {
    try {
      await api.put(`/api/chatbot/${chatbotUuid}`, {
        email,
      });

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Successfully saved",
        submsg: "Changes are saved automatically.",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Please try again later!",
      });
    }
  };

  const saveChatbotName = async value => {
    try {
      await api.put(`/api/chatbot/${chatbotUuid}`, {
        name: value,
      });

      setFeedback({
        visible: true,
        status: "ok",
        msg: "Successfully saved",
        submsg: "Changes are saved automatically.",
      });
    } catch (err) {
      setFeedback({
        visible: true,
        status: "error",
        msg: "Save failed",
        submsg: "Please try again later!",
      });
    }
  };

  const saveCompanyName = value => {
    updateCompany({ name: value }, true).then(err => {
      if (err) {
        setFeedback({
          visible: true,
          status: "error",
          msg: "Save failed",
          submsg: "Please try again later!",
        });
        return;
      }
      setFeedback({
        visible: true,
        status: "ok",
        msg: "Company name updated",
        submsg: "Changes saved automatically.",
      });
    });

    setTimeout(() => {
      setFeedback({ visible: false });
    }, 10000);
  };

  const [feedback, setFeedback] = useState({ visible: false });

  return (
    <>
      <CenterWrapper>
        <GeneralSetupTab
          title={"Setup"}
          pageType={"setup"}
          activeTab={"branding"}
        />

        <div
          aria-live="assertive"
          className="pointer-events-none fixed inset-0 flex items-end px-4 sm:items-start sm:p-6 z-50"
        >
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
            <Transition show={feedback.visible}>
              <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition data-[closed]:data-[enter]:translate-y-2 data-[enter]:transform data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-x-2 data-[closed]:data-[enter]:sm:translate-y-0">
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="shrink-0">
                      {feedback.status === "ok" && (
                        <CheckCircleIcon
                          aria-hidden="true"
                          className="size-6 text-green-400"
                        />
                      )}
                      {feedback.status === "error" && (
                        <XCircleIcon
                          aria-hidden="true"
                          className="size-6 text-red-400"
                        />
                      )}
                    </div>
                    <div className="ml-3 w-0 flex-1 pt-0.5">
                      <p className="mt-1 text-sm text-gray-500">
                        {feedback.msg}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {feedback.submsg}
                      </p>
                    </div>
                    <div className="ml-4 flex shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setFeedback({ visible: false });
                        }}
                        className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon aria-hidden="true" className="size-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start m-auto max-w-7xl">
          <div className="w-full">
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0">
                  Brand Name
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  Enter your brand name, this goes in the top section of the
                  chat interface
                </p>
                <Input value={companyName} onChange={handleCompanyChanged} />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0">
                  Brand Logo
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  This goes into the top header section of the chatbot
                </p>
                <BrandLogoUpload
                  title="Brand Logo"
                  subtitle="This goes into the top header section of the chatbot"
                  defaultValue={companyImageUrl}
                  uploadHandler={uploadCompanyFile}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0">
                  AI Agent Name
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  Name your new AI employee so they can have a personality. Or
                  you can simply choose to name them after your business.
                </p>
                <Input
                  name="aiEmployeeName"
                  register={register}
                  placeholder="Molly"
                  onChange={handleAiEmployeeNameChanged}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0">
                  AI Agent Email
                </h2>
                <Input
                  name="email"
                  register={register}
                  placeholder="test@gmail.com"
                  onChange={handleEmailChanged}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 mb-2">
                  AI Agent Profile Photo
                </h2>
                <BrandLogoUpload
                  title="AI Agent Profile Photo"
                  defaultValue={chatbotPictureUrl}
                  uploadHandler={uploadChatbotFile}
                />
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 mb-2">
                  Chat Icon Color
                </h2>

                <ColorPicker
                  color={chatButtonColor}
                  setColor={handleChatButtonColorChanged}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 mb-2">
                  Chat Send Button & Message Color
                </h2>

                <ColorPicker
                  color={buttonColor}
                  setColor={handleButtonColorChanged}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0">
                  AI Agent Instructions
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  This helps your AI employee better understand how you want
                  them to respond to customers to be in alignment with your
                  brand tone.
                </p>

                <TextArea
                  rows={18}
                  name="prompts"
                  register={register}
                  placeholder="Enter a message..."
                  onChange={handleBrandVoiceChanged}
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 flex items-center gap-2">
                  <span>Intro Message</span>
                  <Tooltip
                    theme={{
                      target: "flex",
                    }}
                    content={
                      <div className="max-w-sm">
                        <p className="p-0 m-0 font-semibold">
                          Available variables
                        </p>
                        <span className="text-xs">
                          To add your Agent Name to the message use
                          %agena_name%. To add your Brand Name to the message
                          use %brand_name%.
                        </span>
                      </div>
                    }
                  >
                    <QuestionMarkCircleIcon className="size-5 cursor-pointer" />
                  </Tooltip>
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  When customers open your live chat, this is the first message
                  they will see.
                </p>

                <TextArea
                  rows={2}
                  name="greeting"
                  register={register}
                  onChange={handleIntroMesageChanged}
                  placeholder="Enter a message..."
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 flex items-center gap-2">
                  <span>Hand off within business hours message</span>
                  <Tooltip
                    theme={{
                      target: "flex",
                    }}
                    content={
                      <div className="max-w-sm">
                        <p className="p-0 m-0 font-semibold">
                          Live Support Hours
                        </p>
                        <span className="text-xs">
                          To show your working hours in the format 9am-7pm PST,
                          set the variable %time_range% in the correct place in
                          the message.
                        </span>
                        <br />
                        <br />
                        <span className="text-xs">
                          If you haven't set up your live support hours,{" "}
                          <Link
                            to="/settings/company_info"
                            className="underline"
                          >
                            set them up here.
                          </Link>
                        </span>
                      </div>
                    }
                  >
                    <QuestionMarkCircleIcon className="size-5 cursor-pointer" />
                  </Tooltip>
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  Set the message the bot displays when handing off to a human
                  agent and requesting the user’s email within your business
                  hours.
                </p>

                <TextArea
                  name="hand_off_within_hours"
                  register={register}
                  onChange={saveChatbotChangesDebounced}
                  placeholder="Enter a message..."
                />
              </div>

              <div className="pt-5">
                <h2 className="text-lg font-semibold text-gray-900 leading-7 p-0 m-0 flex items-center gap-2">
                  <span>Hand off outside of business hours message</span>
                  <Tooltip
                    theme={{
                      target: "flex",
                    }}
                    content={
                      <div className="max-w-sm">
                        <p className="p-0 m-0 font-semibold">
                          Live Support Hours
                        </p>
                        <span className="text-xs">
                          To show your working hours in the format 9am-7pm PST,
                          set the variable %time_range% in the correct place in
                          the message.
                        </span>
                        <br />
                        <br />
                        <span className="text-xs">
                          If you haven't set up your live support hours,{" "}
                          <Link
                            to="/settings/company_info"
                            className="underline"
                          >
                            set them up here.
                          </Link>
                        </span>
                      </div>
                    }
                  >
                    <QuestionMarkCircleIcon className="size-5 cursor-pointer" />
                  </Tooltip>
                </h2>
                <p className="text-sm leading-tight text-gray-600 p-0 m-0 mb-2">
                  Set the message the bot displays when handing off to a human
                  agent and requesting the user’s email outside of your business
                  hours.
                </p>

                <TextArea
                  rows={8}
                  name="hand_off_outside_hours"
                  register={register}
                  onChange={saveChatbotChangesDebounced}
                  placeholder="Enter a message..."
                />
              </div>
            </div>
          </div>

          {windowSize?.width > 640 && (
            <div className="w-full">
              <ChatWidget test key={chatWidgetKey} disabled />

              <div className="mt-6 float-right">
                <ChatButton defaultValue={chatButtonColor} />
              </div>
            </div>
          )}

          {windowSize?.width <= 640 && (
            <>
              <ChatWidgetModal
                open={showWidgetModal}
                onClose={onCloseWidgetModal}
                test
                disabled
              />

              <button
                type="button"
                className="fixed bottom-6 right-6"
                onClick={() => setShowWidgetModal(true)}
              >
                <ChatButton defaultValue={chatButtonColor} />
              </button>
            </>
          )}
        </div>
      </CenterWrapper>
    </>
  );
};

export default Branding;
